import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTopicDetailedStats, useTopicExamTimeline } from '../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../stores/pyqAnalyticsStore'
import { ExamTimelineChart } from '../components/PastExamsDashboard/charts'
import { PriorityBadge, TrendBadge } from '../components/PastExamsDashboard/shared/badges'
import { DIFFICULTY_COLORS, SUBJECT_COLORS } from '../constants/colors'
import type { DifficultyLevel } from '../types'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

// Helper to get subject color from various code formats
function getSubjectColor(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return SUBJECT_COLORS.NEET_UG_PHY
  if (code.includes('CHEM')) return SUBJECT_COLORS.NEET_UG_CHEM
  if (code.includes('BIO')) return SUBJECT_COLORS.NEET_UG_BIO
  return '#6B7280'
}

// Helper to get subject name from code
function getSubjectName(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return 'Physics'
  if (code.includes('CHEM')) return 'Chemistry'
  if (code.includes('BIO')) return 'Biology'
  return subjectCode || 'Unknown'
}

export default function TopicDetailPage() {
  const { topicCode } = useParams<{ topicCode: string }>()
  const navigate = useNavigate()
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()

  // Track ViewContent for FB Pixel
  useEffect(() => {
    if (topicCode) {
      fbTrackViewContent('topic_detail', topicCode, `Topic: ${topicCode}`)
    }
  }, [topicCode])

  const { data, isLoading: isStatsLoading } = useTopicDetailedStats(topicCode || null, {
    yearRangeStart,
    yearRangeEnd,
  })

  const { data: timeline, isLoading: isTimelineLoading } = useTopicExamTimeline(topicCode || null, {
    yearRangeStart,
    yearRangeEnd,
  })

  const isLoading = isStatsLoading || isTimelineLoading

  // Extract topic data from nested response
  const topic = data?.topic
  const difficultyDistribution = data?.difficultyDistribution || []
  const examWiseDistribution = data?.examWiseDistribution || {}

  // Convert examWiseDistribution object to sorted array
  const examDistributionArray = Object.entries(examWiseDistribution)
    .map(([examName, count]) => ({ examName, count }))
    .sort((a, b) => {
      // Extract year from exam name for sorting
      const yearA = parseInt(a.examName.match(/\d{4}/)?.[0] || '0')
      const yearB = parseInt(b.examName.match(/\d{4}/)?.[0] || '0')
      return yearA - yearB
    })

  const subjectColor = getSubjectColor(topic?.subjectCode)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {topic?.topicName || 'Loading...'}
              </h1>
              {topic && (
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
                    style={{
                      backgroundColor: subjectColor + '20',
                      color: subjectColor,
                    }}
                  >
                    {getSubjectName(topic.subjectCode)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : topic ? (
          <div className="space-y-6">
            {/* Badges */}
            {(topic.priorityLevel || topic.trendDirection) && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex flex-wrap items-center gap-3">
                  {topic.priorityLevel && <PriorityBadge priority={topic.priorityLevel} size="md" />}
                  {topic.trendDirection && <TrendBadge trend={topic.trendDirection} size="md" />}
                </div>
              </div>
            )}

            {/* Key Stats - Row 1 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-sm font-medium text-gray-700 mb-4">Overview</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">{topic.totalQuestions ?? 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Total Questions</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">
                    {(topic.avgQuestionsPerExam ?? 0).toFixed(1)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Avg/Exam</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-purple-600">
                    {(topic.appearanceFrequency ?? 0).toFixed(0)}%
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Appearance</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-orange-600">
                    {(topic.importanceScore ?? 0).toFixed(1)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Importance</p>
                </div>
              </div>
            </div>

            {/* Key Stats - Row 2 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-sm font-medium text-gray-700 mb-4">History</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">{topic.totalExams ?? 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Total Exams</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">{topic.lastAppearedYear ?? 'N/A'}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Last Appeared</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">{topic.firstAppearedYear ?? 'N/A'}</p>
                  <p className="text-xs sm:text-sm text-gray-600">First Appeared</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-700">{topic.yearsSinceLastAppearance ?? 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Years Since Last</p>
                </div>
              </div>
            </div>

            {/* Difficulty Distribution */}
            {difficultyDistribution.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-sm font-medium text-gray-700 mb-4">Difficulty Distribution</h2>
                <div className="flex gap-3">
                  {difficultyDistribution.map((diff) => (
                    <div
                      key={diff.name}
                      className="flex-1 rounded-lg p-4 text-center"
                      style={{
                        backgroundColor: (DIFFICULTY_COLORS[diff.name as DifficultyLevel] || diff.color || '#6B7280') + '20',
                      }}
                    >
                      <p
                        className="text-2xl font-bold"
                        style={{ color: DIFFICULTY_COLORS[diff.name as DifficultyLevel] || diff.color || '#6B7280' }}
                      >
                        {diff.value ?? 0}
                      </p>
                      <p className="text-xs text-gray-600">{diff.name || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{(diff.percentage ?? 0).toFixed(0)}%</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exam Timeline Chart */}
            {timeline && timeline.timeline && timeline.timeline.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-sm font-medium text-gray-700 mb-4">Questions Over Time</h2>
                <ExamTimelineChart
                  data={timeline.timeline.map((t) => ({
                    year: t.year,
                    questionCount: t.questionCount,
                    examName: t.examName,
                  }))}
                />
              </div>
            )}

            {/* Exam-wise Distribution */}
            {examDistributionArray.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-sm font-medium text-gray-700 mb-4">Exam-wise Distribution</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {examDistributionArray.map(({ examName, count }) => (
                    <div
                      key={examName}
                      className="rounded-lg p-3 text-center"
                      style={{ backgroundColor: subjectColor + '10' }}
                    >
                      <p className="text-lg font-bold" style={{ color: subjectColor }}>
                        {count}
                      </p>
                      <p className="text-xs text-gray-500 truncate" title={examName}>
                        {examName.replace('NEET_', '').replace(/_/g, ' ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500">
            Topic not found
          </div>
        )}
      </div>
    </div>
  )
}
